import 'react-native-url-polyfill/auto';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import { Platform } from 'react-native';

// Get Supabase URL and key from environment variables
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('Missing Supabase URL or anon key. Please check your environment variables.');
}

// Create storage adapter using AsyncStorage for native, localStorage for web
const AsyncStorageAdapter = {
  getItem: async (key: string) => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return window.localStorage.getItem(key);
      }
      const value = await AsyncStorage.getItem(key);
      return value;
    } catch (error) {
      console.error(`Storage error getting ${key}:`, error);
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.localStorage.setItem(key, value);
        return;
      }
      return await AsyncStorage.setItem(key, value);
    } catch (error) {
      console.error(`Storage error setting ${key}:`, error);
    }
  },
  removeItem: async (key: string) => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
        return;
      }
      return await AsyncStorage.removeItem(key);
    } catch (error) {
      console.error(`Storage error removing ${key}:`, error);
    }
  },
};

// Create the Supabase client with improved configuration
export const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
  auth: {
    storage: AsyncStorageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'implicit',
    storageKey: 'supabase.auth.token',
    debug: false,
  },
  global: {
    // Increase timeouts for more reliable auth in mobile environments
    fetch: async (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
      const urlString = url.toString();
      const isAuthRequest = urlString.includes('/auth/v1/');
      
      // Longer timeout for auth requests (token refresh can be slow)
      const timeout = isAuthRequest ? 60000 : 30000; // 60s for auth, 30s for others
      const maxRetries = isAuthRequest ? 2 : 0; // Retry auth requests up to 2 times
      
      let lastError: Error | null = null;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await new Promise<Response>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              reject(new Error(`Request timed out for: ${urlString} (attempt ${attempt + 1})`));
            }, timeout);
            
            fetch(url, options || {})
              .then(response => {
                clearTimeout(timeoutId);
                resolve(response);
              })
              .catch(error => {
                clearTimeout(timeoutId);
                reject(error);
              });
          });
          
          // If we got a response (even error status), return it
          return response;
        } catch (error: any) {
          lastError = error;
          
          // Don't retry on last attempt
          if (attempt < maxRetries) {
            // Exponential backoff: wait 1s, then 2s
            const delay = Math.pow(2, attempt) * 1000;
            console.warn(`[Supabase] Auth request failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries + 1})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
      }
      
      // All retries exhausted
      if (lastError) {
        console.error(`[Supabase] Request failed after ${maxRetries + 1} attempts:`, lastError.message);
        throw lastError;
      }
      
      throw new Error(`Request failed for: ${urlString}`);
    },
  },
});

// Remove verbose logging
// Handle potential domain mismatches
// if (SUPABASE_URL) {
//   const projectRef = SUPABASE_URL.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
//   if (projectRef) {
//     console.log('Supabase project ref:', projectRef);
//   }
// } 